/**
 * useCustomers Hook
 * 客戶管理 React Query Hook
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Customer, PaginatedResponse, CustomerFormData } from "@/lib/types";

const CUSTOMERS_KEY = "/api/customers";

/**
 * 取得客戶列表
 */
export function useCustomers(options: {
    search?: string;
    isActive?: string;
    page?: number;
    limit?: number;
} = {}) {
    const { search = "", isActive, page = 1, limit = 20 } = options;

    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (isActive !== undefined) params.set("isActive", isActive);
    params.set("page", String(page));
    params.set("limit", String(limit));

    const queryString = params.toString();

    return useQuery<PaginatedResponse<Customer>>({
        // 兩元素 key：mutation 用 [CUSTOMERS_KEY] 前綴 invalidate 才打得中帶參數的列表
        queryKey: [CUSTOMERS_KEY, queryString],
        // FIX-08: 補上 queryFn，否則 React Query 不知道如何 fetch，永遠回傳 undefined
        queryFn: async () => {
            const res = await apiRequest("GET", `${CUSTOMERS_KEY}?${queryString}`);
            return res.json() as Promise<PaginatedResponse<Customer>>;
        },
    });
}

/**
 * 建立客戶
 */
export function useCreateCustomer() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CustomerFormData) => {
            const res = await apiRequest("POST", CUSTOMERS_KEY, data);
            return (await res.json()) as Customer;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY], exact: false });
        },
    });
}

/**
 * 更新客戶
 */
export function useUpdateCustomer() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: number; data: Partial<CustomerFormData & { isActive: boolean }> }) => {
            const res = await apiRequest("PATCH", `${CUSTOMERS_KEY}/${id}`, data);
            return (await res.json()) as Customer;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY], exact: false });
        },
    });
}

/**
 * 刪除（軟刪除）客戶
 */
export function useDeleteCustomer() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            await apiRequest("DELETE", `${CUSTOMERS_KEY}/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY], exact: false });
        },
    });
}
